import { useState, useEffect } from 'react';
import { ArrowLeft, Play, Pause, Check, Flame, Award, ChevronRight, ChevronLeft, Sun, X, Music, Volume2, BookOpen, Zap, PlayCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface YogaProps {
  onBack: () => void;
}

type MusicType = 'smooth' | 'chanting';
type PracticeMode = 'learn' | 'pro';

const suryaNamaskarSteps = [
  {
    name: 'Pranamasana',
    english: 'Prayer Pose',
    description: 'Stand at the edge of your mat, keep your feet together and balance your weight equally on both feet. Expand your chest and relax your shoulders. Bring your palms together in front of your chest in prayer position.',
    breathing: 'Normal breathing',
    image: '🙏'
  },
  {
    name: 'Hastauttanasana',
    english: 'Raised Arms Pose',
    description: 'Breathing in, lift your arms up and back, keeping the biceps close to your ears. The effort is to stretch the whole body from heels to fingertips.',
    breathing: 'Inhale',
    image: '🙆'
  },
  {
    name: 'Hastapadasana',
    english: 'Standing Forward Bend',
    description: 'Breathing out, bend forward from the waist keeping the spine straight. As you exhale, bring your hands down to the floor beside your feet.',
    breathing: 'Exhale',
    image: '🙇'
  },
  {
    name: 'Ashwa Sanchalanasana',
    english: 'Equestrian Pose',
    description: 'Breathing in, push your right leg back as far as possible. Bring the right knee to the floor and look up.',
    breathing: 'Inhale',
    image: '🏃'
  },
  {
    name: 'Dandasana',
    english: 'Stick Pose',
    description: 'As you breathe in, take the left leg back and bring the whole body in a straight line. Keep your arms perpendicular to the floor.',
    breathing: 'Inhale',
    image: '🧘'
  },
  {
    name: 'Ashtanga Namaskara',
    english: 'Eight Limbed Pose',
    description: 'Gently bring your knees down to the floor and exhale. Take the hips back slightly, slide forward, rest your chest and chin on the floor. Eight parts of your body touch the floor.',
    breathing: 'Exhale',
    image: '🛐'
  },
  {
    name: 'Bhujangasana',
    english: 'Cobra Pose',
    description: 'Slide forward and raise the chest up into the Cobra pose. Keep your elbows bent and shoulders away from ears. Look up at the ceiling.',
    breathing: 'Inhale',
    image: '🐍'
  },
  {
    name: 'Adho Mukha Svanasana',
    english: 'Downward Facing Dog',
    description: 'Breathing out, lift the hips and the tailbone up to bring the body into an inverted "V" pose. Keep the heels on the ground and make a gentle effort to lift the tailbone up.',
    breathing: 'Exhale',
    image: '🐕'
  },
  {
    name: 'Ashwa Sanchalanasana',
    english: 'Equestrian Pose',
    description: 'Breathing in, bring the right foot forward in between the two hands. The left knee goes down on the floor. Press the hips down and look up.',
    breathing: 'Inhale',
    image: '🏃'
  },
  {
    name: 'Hastapadasana',
    english: 'Standing Forward Bend',
    description: 'Breathing out, bring the left foot forward. Keep the palms on the floor. You may bend the knees if necessary.',
    breathing: 'Exhale',
    image: '🙇'
  },
  {
    name: 'Hastauttanasana',
    english: 'Raised Arms Pose',
    description: 'Breathing in, roll the spine up. Raise your hands up and bend backward a little bit, pushing the hips slightly outward.',
    breathing: 'Inhale',
    image: '🙆'
  },
  {
    name: 'Pranamasana',
    english: 'Prayer Pose',
    description: 'As you exhale, first straighten the body, then bring the arms down. Relax in this position and observe the sensations in your body.',
    breathing: 'Exhale',
    image: '🙏'
  }
];

export default function Yoga({ onBack }: YogaProps) {
  const [duration, setDuration] = useState(10);
  const [isActive, setIsActive] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [streak, setStreak] = useState(0);
  const [weeklyStats, setWeeklyStats] = useState({ total_sessions: 0, total_minutes: 0 });
  const [isCompleting, setIsCompleting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState<MusicType | null>(null);
  const [showLearnModal, setShowLearnModal] = useState(false);
  const [practiceMode, setPracticeMode] = useState<PracticeMode>('pro');
  const [showPracticeView, setShowPracticeView] = useState(false);

  useEffect(() => {
    loadStreak();
    loadWeeklyStats();
  }, []);

  useEffect(() => {
    let interval: number | undefined;
    if (isActive && timeElapsed < duration * 60) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    } else if (timeElapsed >= duration * 60) {
      setIsActive(false);
    }
    return () => clearInterval(interval);
  }, [isActive, timeElapsed, duration]);

  const loadStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_activity_streak', {
        p_user_id: user.id,
        p_activity_type: 'yoga'
      });

      if (!error && data !== null) {
        setStreak(data);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_activity_weekly_stats', {
        p_user_id: user.id,
        p_activity_type: 'yoga'
      });

      if (!error && data && data.length > 0) {
        setWeeklyStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  const handleStartClick = () => {
    if (practiceMode === 'learn') {
      setShowLearnModal(true);
    } else {
      setShowMusicModal(true);
    }
  };

  const handleMusicSelect = (type: MusicType) => {
    setSelectedMusic(type);
    setShowMusicModal(false);
    setShowPracticeView(true);
    setIsActive(true);
  };

  const handlePause = () => {
    setIsActive(false);
  };

  const handleResume = () => {
    setIsActive(true);
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.from('mind_body_activities').insert({
        user_id: user.id,
        activity_type: 'yoga',
        duration_minutes: Math.ceil(timeElapsed / 60),
        difficulty_level: 'surya_namaskar',
        completed_at: new Date().toISOString()
      });

      if (!error) {
        await loadStreak();
        await loadWeeklyStats();
        setTimeElapsed(0);
        setIsActive(false);
        setShowPracticeView(false);
        setCurrentStep(0);

        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving yoga session:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleExitPractice = () => {
    setIsActive(false);
    setShowPracticeView(false);
    setTimeElapsed(0);
    setCurrentStep(0);
    setSelectedMusic(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (timeElapsed / (duration * 60)) * 100;
  const canComplete = timeElapsed >= duration * 60;

  const nextStep = () => {
    if (currentStep < suryaNamaskarSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Music Selection Modal
  const MusicModal = () => (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => setShowMusicModal(false)}>
      <div 
        className="w-full max-w-lg bg-white rounded-t-3xl p-6 transform transition-transform"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center mb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Music size={28} className="text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Choose Your Sound</h2>
          <p className="text-sm text-gray-500 mt-1">Select the background music for your practice</p>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => handleMusicSelect('smooth')}
            className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-all flex items-center gap-4 active:scale-[0.98]"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center">
              <Music size={24} className="text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-gray-900">Smooth Music</p>
              <p className="text-xs text-gray-500">Calm instrumental background</p>
            </div>
            <ChevronRight size={20} className="text-gray-300" />
          </button>

          <button
            onClick={() => handleMusicSelect('chanting')}
            className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all flex items-center gap-4 active:scale-[0.98]"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center">
              <Volume2 size={24} className="text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-gray-900">Original Chanting</p>
              <p className="text-xs text-gray-500">Traditional Sanskrit mantras</p>
            </div>
            <ChevronRight size={20} className="text-gray-300" />
          </button>
        </div>

        <button
          onClick={() => setShowMusicModal(false)}
          className="w-full py-3 text-gray-500 font-semibold text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Learn Mode Modal
  const LearnModal = () => (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => setShowLearnModal(false)}>
      <div 
        className="w-full max-w-lg bg-white rounded-t-3xl p-6 transform transition-transform"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center mb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <button
          onClick={() => setShowLearnModal(false)}
          className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <X size={16} className="text-gray-600" />
        </button>
        
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <BookOpen size={28} className="text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Learn Surya Namaskar</h2>
          <p className="text-sm text-gray-500 mt-1">Watch video tutorials for each pose</p>
        </div>

        {/* Video Placeholder */}
        <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl aspect-video flex flex-col items-center justify-center mb-6 border-2 border-dashed border-gray-300">
          <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center mb-3 shadow-lg">
            <PlayCircle size={32} className="text-gray-400" />
          </div>
          <p className="text-gray-500 font-semibold">Video Coming Soon</p>
          <p className="text-xs text-gray-400 mt-1">Learn the perfect form</p>
        </div>

        {/* Steps Preview */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-xs font-bold text-gray-500 mb-3">12 POSES TO LEARN</p>
          <div className="grid grid-cols-6 gap-2">
            {suryaNamaskarSteps.map((step, index) => (
              <div
                key={index}
                className="aspect-square bg-white rounded-lg flex items-center justify-center text-lg border border-gray-200"
                title={step.name}
              >
                {step.image}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => setShowLearnModal(false)}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all"
        >
          Got It
        </button>
      </div>
    </div>
  );

  // Practice View (Pro Mode Active Session)
  if (showPracticeView) {
    const step = suryaNamaskarSteps[currentStep];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <div className="max-w-lg mx-auto pb-8">
          {/* Header */}
          <header className="bg-white/80 backdrop-blur-lg px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-orange-200/50">
            <button
              onClick={handleExitPractice}
              className="w-9 h-9 rounded-xl bg-orange-100 hover:bg-orange-200 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-orange-700" />
            </button>
            <div className="text-center">
              <p className="text-xs text-gray-500">Step {currentStep + 1} of {suryaNamaskarSteps.length}</p>
              <p className="text-base font-bold text-gray-900">{formatTime(timeElapsed)}</p>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-lg">
              {selectedMusic === 'smooth' ? (
                <Music size={14} className="text-orange-600" />
              ) : (
                <Volume2 size={14} className="text-orange-600" />
              )}
              <span className="text-[10px] font-bold text-orange-600 uppercase">
                {selectedMusic}
              </span>
            </div>
          </header>

          <div className="p-4 space-y-4">
            {/* Progress Bar */}
            <div className="bg-white rounded-full p-1 border border-orange-200">
              <div className="flex gap-0.5">
                {suryaNamaskarSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 flex-1 rounded-full transition-all ${
                      index <= currentStep ? 'bg-gradient-to-r from-orange-400 to-amber-400' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Timer Progress */}
            <div className="bg-white rounded-xl p-3 border border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500">Session Progress</span>
                <span className="text-xs font-bold text-orange-600">{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-orange-400 to-amber-400 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Step Card */}
            <div className="bg-white rounded-2xl border-2 border-orange-200 overflow-hidden">
              {/* Step Image/Emoji */}
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 py-10 flex items-center justify-center">
                <span className="text-8xl">{step.image}</span>
              </div>

              {/* Step Content */}
              <div className="p-5">
                {/* Breathing Tag */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    step.breathing === 'Inhale' 
                      ? 'bg-blue-100 text-blue-700' 
                      : step.breathing === 'Exhale'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {step.breathing}
                  </span>
                </div>

                {/* Name */}
                <h2 className="text-xl font-bold text-gray-900">{step.name}</h2>
                <p className="text-sm text-orange-600 font-medium mb-3">{step.english}</p>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  currentStep === 0
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                    : 'bg-white border-2 border-orange-200 text-orange-600 hover:bg-orange-50 active:scale-[0.95]'
                }`}
              >
                <ChevronLeft size={20} />
              </button>

              {!isActive ? (
                <button
                  onClick={handleResume}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Play size={18} fill="currentColor" />
                  Resume
                </button>
              ) : (
                <button
                  onClick={handlePause}
                  className="flex-1 bg-gradient-to-r from-gray-700 to-gray-800 text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Pause size={18} fill="currentColor" />
                  Pause
                </button>
              )}

              <button
                onClick={nextStep}
                disabled={currentStep === suryaNamaskarSteps.length - 1}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  currentStep === suryaNamaskarSteps.length - 1
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                    : 'bg-white border-2 border-orange-200 text-orange-600 hover:bg-orange-50 active:scale-[0.95]'
                }`}
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Complete Button */}
            {canComplete && (
              <button
                onClick={handleComplete}
                disabled={isCompleting}
                className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isCompleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Complete Session
                  </>
                )}
              </button>
            )}

            {/* Quick Jump */}
            <div className="bg-white rounded-xl p-4 border border-orange-200">
              <p className="text-xs font-semibold text-gray-500 mb-3">Quick Jump</p>
              <div className="grid grid-cols-6 gap-2">
                {suryaNamaskarSteps.map((s, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`aspect-square rounded-lg font-bold text-xs transition-all flex items-center justify-center ${
                      index === currentStep
                        ? 'bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg'
                        : index < currentStep
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {s.image}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main View
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <div className="max-w-lg mx-auto pb-8">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-lg px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-orange-200/50">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-orange-100 hover:bg-orange-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={18} className="text-orange-700" />
          </button>
          <div className="flex items-center gap-2">
            <Sun size={20} className="text-orange-500" />
            <h1 className="text-base font-bold text-gray-900">Surya Namaskar</h1>
          </div>
          <div className="w-9"></div>
        </header>

        <div className="p-4 space-y-4">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-orange-400 via-amber-400 to-yellow-400 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-xl font-black mb-1">Sun Salutation</h2>
              <p className="text-orange-100 text-sm mb-4">A complete body workout in 12 powerful poses</p>
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur rounded-xl px-3 py-2">
                  <p className="text-xs text-orange-100">Streak</p>
                  <p className="text-lg font-black flex items-center gap-1">
                    <Flame size={16} />
                    {streak} days
                  </p>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-xl px-3 py-2">
                  <p className="text-xs text-orange-100">This Week</p>
                  <p className="text-lg font-black flex items-center gap-1">
                    <Award size={16} />
                    {weeklyStats.total_sessions} sessions
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute -right-6 -bottom-6 text-8xl opacity-20">☀️</div>
          </div>

          {/* Mode Selection */}
          <div className="bg-white rounded-2xl p-4 border border-orange-200">
            <p className="text-xs font-bold text-gray-500 mb-3">SELECT MODE</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPracticeMode('learn')}
                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  practiceMode === 'learn'
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                  practiceMode === 'learn' ? 'bg-blue-500' : 'bg-gray-200'
                }`}>
                  <BookOpen size={20} className={practiceMode === 'learn' ? 'text-white' : 'text-gray-500'} />
                </div>
                <p className={`font-bold text-sm ${practiceMode === 'learn' ? 'text-blue-700' : 'text-gray-700'}`}>Learn Mode</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Watch & learn poses</p>
              </button>

              <button
                onClick={() => setPracticeMode('pro')}
                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  practiceMode === 'pro'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                  practiceMode === 'pro' ? 'bg-orange-500' : 'bg-gray-200'
                }`}>
                  <Zap size={20} className={practiceMode === 'pro' ? 'text-white' : 'text-gray-500'} />
                </div>
                <p className={`font-bold text-sm ${practiceMode === 'pro' ? 'text-orange-700' : 'text-gray-700'}`}>Pro Mode</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Guided practice</p>
              </button>
            </div>
          </div>

          {/* Duration Selection */}
          <div className="bg-white rounded-2xl p-4 border border-orange-200">
            <p className="text-xs font-bold text-gray-500 mb-3">DURATION</p>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 15, 20].map((min) => (
                <button
                  key={min}
                  onClick={() => setDuration(min)}
                  className={`py-3 rounded-xl font-bold text-sm transition-all active:scale-[0.95] ${
                    duration === min
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {min} min
                </button>
              ))}
            </div>
          </div>

          {/* 12 Poses Preview */}
          <div className="bg-white rounded-2xl p-4 border border-orange-200">
            <p className="text-xs font-bold text-gray-500 mb-3">12 POSES</p>
            <div className="grid grid-cols-6 gap-2">
              {suryaNamaskarSteps.map((step, index) => (
                <div
                  key={index}
                  className="aspect-square bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl flex flex-col items-center justify-center border border-orange-100"
                >
                  <span className="text-xl">{step.image}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={handleStartClick}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-4 rounded-2xl font-bold text-base transition-all shadow-lg shadow-orange-500/30 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <Play size={20} fill="currentColor" />
            Start {practiceMode === 'learn' ? 'Learning' : 'Session'}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showMusicModal && <MusicModal />}
      {showLearnModal && <LearnModal />}
    </div>
  );
}